// ==================================
// Pagina Detalle de Veterinaria
// ==================================

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Header } from '@/componentes/layout';
import {
  Button,
  Card,
  Badge,
  Modal,
  Input,
  Spinner,
} from '@/componentes/ui';
import { veterinariasServicio } from '@/servicios';
import type { EstadoLicencia, TipoLicencia } from '@/tipos';

// Iconos
const IconVolver = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const IconEditar = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const IconActivar = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const IconDesactivar = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
  </svg>
);

const IconGratis = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
  </svg>
);

const IconLink = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
  </svg>
);

const IconPago = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const IconCancelar = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const IconConfirmar = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const IconResetPassword = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
  </svg>
);

function getBadgeVariant(
  estado: EstadoLicencia
): 'success' | 'warning' | 'danger' | 'info' | 'gray' {
  const variants: Record<
    EstadoLicencia,
    'success' | 'warning' | 'danger' | 'info' | 'gray'
  > = {
    activa: 'success',
    en_gracia: 'warning',
    expirada: 'danger',
    suspendida: 'danger',
    pendiente: 'gray',
  };
  return variants[estado] || 'gray';
}

function getTipoLabel(tipo: TipoLicencia): string {
  const labels: Record<TipoLicencia, string> = {
    gratis: 'Gratis',
    prueba: 'Prueba',
    mensual: 'Mensual',
    anual: 'Anual',
  };
  return labels[tipo] || tipo;
}

interface InfoItemProps {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
}

function InfoItem({ label, value, icon }: InfoItemProps) {
  return (
    <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg">
      {icon && (
        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-stone-500 shadow-sm">
          {icon}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-stone-500 uppercase tracking-wide">{label}</p>
        <div className="mt-0.5 text-sm font-medium text-obsidian-900">{value}</div>
      </div>
    </div>
  );
}

export function VeterinariaDetalle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showGratisModal, setShowGratisModal] = useState(false);
  const [motivoGratis, setMotivoGratis] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPasswordText, setShowPasswordText] = useState(false);
  const { data: veterinaria, isLoading } = useQuery({
    queryKey: ['veterinaria', id],
    queryFn: () => veterinariasServicio.obtenerPorId(id!),
    enabled: !!id,
  });

  const activarMutation = useMutation({
    mutationFn: () => veterinariasServicio.activar(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['veterinaria', id] });
      queryClient.invalidateQueries({ queryKey: ['veterinarias'] });
      toast.success('Veterinaria activada');
    },
    onError: () => toast.error('Error al activar'),
  });

  const desactivarMutation = useMutation({
    mutationFn: () => veterinariasServicio.desactivar(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['veterinaria', id] });
      queryClient.invalidateQueries({ queryKey: ['veterinarias'] });
      toast.success('Veterinaria desactivada');
    },
    onError: () => toast.error('Error al desactivar'),
  });

  const gratisMutation = useMutation({
    mutationFn: (esGratis: boolean) =>
      veterinariasServicio.marcarGratis(id!, esGratis, motivoGratis || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['veterinaria', id] });
      queryClient.invalidateQueries({ queryKey: ['veterinarias'] });
      setShowGratisModal(false);
      setMotivoGratis('');
      toast.success('Veterinaria actualizada');
    },
    onError: () => toast.error('Error al actualizar'),
  });

  const linkPagoMutation = useMutation({
    mutationFn: () => veterinariasServicio.generarNuevoLinkPago(id!),
    onSuccess: (data) => {
      navigator.clipboard.writeText(data.linkPago);
      toast.success('Link copiado al portapapeles');
    },
    onError: () => toast.error('Error al generar link'),
  });

  const kitPagadoMutation = useMutation({
    mutationFn: (kitPagado: boolean) =>
      veterinariasServicio.marcarKitPagado(id!, kitPagado),
    onSuccess: (_, kitPagado) => {
      queryClient.invalidateQueries({ queryKey: ['veterinaria', id] });
      queryClient.invalidateQueries({ queryKey: ['veterinarias'] });
      toast.success(kitPagado ? 'Kit marcado como pagado' : 'Kit marcado como pendiente');
    },
    onError: () => toast.error('Error al actualizar el estado del kit'),
  });

  const toggleVisibilidadMutation = useMutation({
    mutationFn: () =>
      veterinariasServicio.toggleVisibilidadDirectorio(id!, !veterinaria?.visibleEnDirectorio),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['veterinaria', id] });
      queryClient.invalidateQueries({ queryKey: ['veterinarias'] });
      toast.success(
        veterinaria?.visibleEnDirectorio
          ? 'Veterinaria oculta en Vetztopia'
          : 'Veterinaria visible en Vetztopia'
      );
    },
    onError: () => toast.error('Error al cambiar la visibilidad'),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: () => veterinariasServicio.restablecerPassword(id!, newPassword),
    onSuccess: () => {
      setShowResetPassword(false);
      setNewPassword('');
      setShowPasswordText(false);
      toast.success('Contraseña restablecida exitosamente');
    },
    onError: () => toast.error('Error al restablecer la contraseña'),
  });

  const generarPasswordAleatorio = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
    setShowPasswordText(true);
  };

  if (isLoading) {
    return (
      <div>
        <Header titulo="Cargando..." />
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (!veterinaria) {
    return (
      <div>
        <Header titulo="Veterinaria no encontrada" />
        <div className="p-6">
          <Card className="text-center py-12">
            <p className="text-stone-500 mb-4">
              La veterinaria que buscas no existe
            </p>
            <Button leftIcon={<IconVolver />} onClick={() => navigate('/veterinarias')}>
              Volver a la lista
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header titulo={veterinaria.nombre} />

      <div className="p-4 sm:p-6 space-y-6">
        {/* Header con info principal y acciones */}
        <Card>
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            {/* Info principal */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                {veterinaria.logo?.url ? (
                  <img src={veterinaria.logo.url} alt={veterinaria.nombre} className="w-12 h-12 rounded-xl object-cover" />
                ) : (
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold text-obsidian-900">{veterinaria.nombre}</h2>
                  <p className="text-sm text-stone-500">{veterinaria.slug}.alebrijecloud.com</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Badge variant={veterinaria.activo ? 'success' : 'gray'} dot>
                  {veterinaria.activo ? 'Activo' : 'Inactivo'}
                </Badge>
                <Badge variant={getBadgeVariant(veterinaria.licencia.estado)} dot>
                  {veterinaria.licencia.estado.replace('_', ' ')}
                </Badge>
                <Badge variant="info">
                  {getTipoLabel(veterinaria.licencia.tipo)}
                </Badge>
                <Badge variant={veterinaria.kitPagado ? 'success' : 'warning'}>
                  Kit {veterinaria.kitPagado ? 'Pagado' : 'Pendiente'}
                </Badge>
                {veterinaria.esGratis && (
                  <Badge variant="purple">Acceso Gratis</Badge>
                )}
                <Badge variant={veterinaria.visibleEnDirectorio ? 'success' : 'gray'}>
                  {veterinaria.visibleEnDirectorio ? 'Visible en Vetztopia' : 'Oculto en Vetztopia'}
                </Badge>
              </div>
            </div>

            {/* Acciones principales */}
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" leftIcon={<IconVolver />} onClick={() => navigate('/veterinarias')}>
                Volver
              </Button>
              <Button variant="primary" leftIcon={<IconEditar />} onClick={() => navigate(`/veterinarias/${id}/editar`)}>
                Editar
              </Button>
            </div>
          </div>
        </Card>

        {/* Grid de informacion */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Columna 1: Contacto */}
          <Card>
            <h3 className="text-sm font-semibold text-obsidian-900 uppercase tracking-wide mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Contacto
            </h3>
            <div className="space-y-3">
              <InfoItem
                label="Email"
                value={veterinaria.email}
                icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>}
              />
              <InfoItem
                label="Teléfono"
                value={veterinaria.telefono}
                icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>}
              />
            </div>
          </Card>

          {/* Columna 2: Licencia */}
          <Card>
            <h3 className="text-sm font-semibold text-obsidian-900 uppercase tracking-wide mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              Licencia
            </h3>
            <div className="space-y-3">
              <InfoItem
                label="Tipo"
                value={getTipoLabel(veterinaria.licencia.tipo)}
                icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>}
              />
              <InfoItem
                label="Inicio"
                value={new Date(veterinaria.licencia.fechaInicio).toLocaleDateString('es-MX')}
                icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
              />
              {veterinaria.licencia.fechaExpiracion && (
                <InfoItem
                  label="Expiración"
                  value={new Date(veterinaria.licencia.fechaExpiracion).toLocaleDateString('es-MX')}
                  icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                />
              )}
              {veterinaria.esGratis && veterinaria.motivoGratis && (
                <InfoItem
                  label="Motivo gratis"
                  value={veterinaria.motivoGratis}
                  icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" /></svg>}
                />
              )}
            </div>
          </Card>

          {/* Columna 3: Direccion y Cobertura */}
          <Card>
            <h3 className="text-sm font-semibold text-obsidian-900 uppercase tracking-wide mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Ubicación y Cobertura
            </h3>
            <div className="space-y-3">
              {/* Modalidad de atencion */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-stone-500 uppercase tracking-wide">Modalidad:</span>
                <Badge variant={
                  veterinaria.modalidadAtencion === 'ambos' ? 'info' :
                  veterinaria.modalidadAtencion === 'domicilio' ? 'warning' : 'success'
                }>
                  {veterinaria.modalidadAtencion === 'fisico' && 'Lugar físico'}
                  {veterinaria.modalidadAtencion === 'domicilio' && 'A domicilio'}
                  {veterinaria.modalidadAtencion === 'ambos' && 'Físico + Domicilio'}
                  {!veterinaria.modalidadAtencion && 'Lugar físico'}
                </Badge>
              </div>

              {/* Direccion fisica */}
              {(!veterinaria.modalidadAtencion || veterinaria.modalidadAtencion !== 'domicilio') && (
                <div className="p-3 bg-stone-50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-stone-500 shadow-sm flex-shrink-0">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div className="text-sm text-stone-700 space-y-0.5">
                      <p className="font-medium">
                        {veterinaria.direccion.calle} {veterinaria.direccion.numeroExterior}
                        {veterinaria.direccion.numeroInterior && `, Int. ${veterinaria.direccion.numeroInterior}`}
                      </p>
                      <p>Col. {veterinaria.direccion.colonia}, C.P. {veterinaria.direccion.codigoPostal}</p>
                      <p>{veterinaria.direccion.ciudad}, {veterinaria.direccion.estado}</p>
                      <p className="text-stone-500">{veterinaria.direccion.pais}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Direccion general (solo para domicilio) */}
              {veterinaria.modalidadAtencion === 'domicilio' && (
                <div className="p-3 bg-stone-50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-stone-500 shadow-sm flex-shrink-0">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="text-sm text-stone-700 space-y-0.5">
                      <p>{veterinaria.direccion.ciudad}, {veterinaria.direccion.estado}</p>
                      <p className="text-stone-500">C.P. {veterinaria.direccion.codigoPostal}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Zonas de cobertura */}
              {veterinaria.zonasCobertura && veterinaria.zonasCobertura.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-2">Zonas de cobertura</p>
                  <div className="space-y-2">
                    {veterinaria.zonasCobertura.map((zona, idx) => (
                      <div key={idx} className="p-3 bg-stone-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <svg className="w-3.5 h-3.5 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm font-medium text-obsidian-900">{zona.estado}</span>
                          {zona.todoElEstado && (
                            <Badge variant="info">Todo el estado</Badge>
                          )}
                        </div>
                        {!zona.todoElEstado && zona.ciudades && zona.ciudades.length > 0 && (
                          <div className="ml-5 mt-1 flex flex-wrap gap-1">
                            {zona.ciudades.map((ciudad, cIdx) => (
                              <span
                                key={cIdx}
                                className="inline-flex items-center gap-1 text-xs bg-white px-2 py-0.5 rounded-md border border-stone-200 text-stone-600"
                              >
                                {ciudad.nombre}
                                {ciudad.todaLaCiudad && (
                                  <span className="text-primary-500 font-medium">TODA</span>
                                )}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Acciones adicionales */}
        <Card>
          <h3 className="text-sm font-semibold text-obsidian-900 uppercase tracking-wide mb-4 flex items-center gap-2">
            <svg className="w-4 h-4 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
            Acciones
          </h3>
          <div className="flex flex-wrap gap-3">
            {/* Estado de cuenta */}
            {veterinaria.activo ? (
              <Button
                variant="secondary"
                leftIcon={<IconDesactivar />}
                onClick={() => desactivarMutation.mutate()}
                loading={desactivarMutation.isPending}
              >
                Desactivar cuenta
              </Button>
            ) : (
              <Button
                variant="success"
                leftIcon={<IconActivar />}
                onClick={() => activarMutation.mutate()}
                loading={activarMutation.isPending}
              >
                Activar cuenta
              </Button>
            )}

            {/* Acceso gratis */}
            {veterinaria.esGratis ? (
              <Button
                variant="secondary"
                leftIcon={<IconGratis />}
                onClick={() => gratisMutation.mutate(false)}
                loading={gratisMutation.isPending}
              >
                Quitar acceso gratis
              </Button>
            ) : (
              <Button
                variant="secondary"
                leftIcon={<IconGratis />}
                onClick={() => setShowGratisModal(true)}
              >
                Marcar como gratis
              </Button>
            )}

            {/* Kit de instalacion */}
            {!veterinaria.kitPagado ? (
              <>
                <Button
                  variant="secondary"
                  leftIcon={<IconLink />}
                  onClick={() => linkPagoMutation.mutate()}
                  loading={linkPagoMutation.isPending}
                >
                  Generar link de pago
                </Button>
                <Button
                  variant="success"
                  leftIcon={<IconPago />}
                  onClick={() => kitPagadoMutation.mutate(true)}
                  loading={kitPagadoMutation.isPending}
                >
                  Marcar kit pagado
                </Button>
              </>
            ) : (
              <Button
                variant="secondary"
                leftIcon={<IconPago />}
                onClick={() => kitPagadoMutation.mutate(false)}
                loading={kitPagadoMutation.isPending}
              >
                Desmarcar kit pagado
              </Button>
            )}

            {/* Restablecer contraseña */}
            <Button
              variant="secondary"
              leftIcon={<IconResetPassword />}
              onClick={() => setShowResetPassword(true)}
            >
              Restablecer contraseña
            </Button>

            {/* Vetztopia visibilidad */}
            <Button
              variant={veterinaria.visibleEnDirectorio ? 'secondary' : 'primary'}
              leftIcon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              onClick={() => toggleVisibilidadMutation.mutate()}
              loading={toggleVisibilidadMutation.isPending}
            >
              {veterinaria.visibleEnDirectorio ? 'Ocultar en Vetztopia' : 'Mostrar en Vetztopia'}
            </Button>

          </div>
        </Card>

        {/* Info de registro */}
        <div className="flex flex-wrap gap-4 text-sm text-stone-500">
          <span>
            Registrado: {new Date(veterinaria.createdAt).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
          <span>
            Actualizado: {new Date(veterinaria.updatedAt).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
        </div>
      </div>

      {/* Modal para marcar como gratis */}
      <Modal
        isOpen={showGratisModal}
        onClose={() => setShowGratisModal(false)}
        title="Marcar como acceso gratuito"
      >
        <div className="space-y-4">
          <p className="text-stone-600">
            Al marcar esta veterinaria como gratuita, no se le cobrará la suscripción mensual.
          </p>
          <Input
            label="Motivo (opcional)"
            placeholder="Ej: Convenio especial, beta tester, etc."
            value={motivoGratis}
            onChange={(e) => setMotivoGratis(e.target.value)}
            icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>}
          />
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              leftIcon={<IconCancelar />}
              onClick={() => setShowGratisModal(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              leftIcon={<IconConfirmar />}
              onClick={() => gratisMutation.mutate(true)}
              loading={gratisMutation.isPending}
            >
              Confirmar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal para restablecer contraseña */}
      <Modal
        isOpen={showResetPassword}
        onClose={() => {
          setShowResetPassword(false);
          setNewPassword('');
          setShowPasswordText(false);
        }}
        title="Restablecer contraseña"
      >
        <div className="space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-sm text-amber-800">
              Esto cambiará la contraseña del administrador de la veterinaria. Asegúrate de comunicarle la nueva contraseña.
            </p>
          </div>
          <Input
            label="Nueva contraseña"
            type={showPasswordText ? 'text' : 'password'}
            placeholder="Mínimo 8 caracteres"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>}
            suffix={
              <button
                type="button"
                onClick={() => setShowPasswordText(!showPasswordText)}
                className="text-stone-400 hover:text-stone-600 transition-colors"
              >
                {showPasswordText ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l18 18" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            }
          />
          <Button
            variant="secondary"
            onClick={generarPasswordAleatorio}
            className="w-full"
          >
            Generar contraseña aleatoria
          </Button>
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              leftIcon={<IconCancelar />}
              onClick={() => {
                setShowResetPassword(false);
                setNewPassword('');
                setShowPasswordText(false);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              leftIcon={<IconConfirmar />}
              onClick={() => resetPasswordMutation.mutate()}
              loading={resetPasswordMutation.isPending}
              disabled={newPassword.length < 8}
            >
              Restablecer
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  );
}

export default VeterinariaDetalle;
